import React, { useState } from "react";

import { useCloud } from "../../cloud/context";

import "./LoginScreen.scss";

type Step =
  | { type: "credentials" }
  | {
      type: "newPassword";
      completeChallenge: (newPassword: string) => Promise<any>;
    };

export const LoginScreen: React.FC = () => {
  const { login, completeSignIn, error, clearError } = useCloud();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<Step>({ type: "credentials" });
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError(null);
    setBusy(true);
    try {
      const result = await login(username, password);
      if (result.type === "newPasswordRequired") {
        setStep({ type: "newPassword", completeChallenge: result.completeChallenge });
      }
    } catch {
      // error set in context
    } finally {
      setBusy(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (newPassword !== newPasswordConfirm) {
      setLocalError("Passwords don't match");
      return;
    }
    if (newPassword.length < 12) {
      setLocalError("Password must be at least 12 characters");
      return;
    }
    if (step.type !== "newPassword") return;
    setBusy(true);
    try {
      const cloudUser = await step.completeChallenge(newPassword);
      completeSignIn(cloudUser);
    } catch (err: any) {
      setLocalError(err?.message ?? "Failed to set password");
      setBusy(false);
    }
  };

  const displayError = localError ?? error;

  if (step.type === "newPassword") {
    return (
      <div className="cloud-login-overlay">
        <div className="cloud-login-card">
          <div className="cloud-login-logo">
            <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
              <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
            </svg>
          </div>
          <h1 className="cloud-login-title">Set new password</h1>
          <p className="cloud-login-subtitle">
            Choose a permanent password for <strong>{username}</strong>
          </p>

          <form onSubmit={handleNewPassword} className="cloud-login-form">
            {displayError && (
              <div className="cloud-login-error" role="alert">
                {displayError}
              </div>
            )}

            <div className="cloud-login-field">
              <label htmlFor="cloud-new-password">New password</label>
              <input
                id="cloud-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
                required
                disabled={busy}
                placeholder="Min. 12 characters"
              />
            </div>

            <div className="cloud-login-field">
              <label htmlFor="cloud-confirm-password">Confirm password</label>
              <input
                id="cloud-confirm-password"
                type="password"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                autoComplete="new-password"
                required
                disabled={busy}
              />
            </div>

            <button
              type="submit"
              className="cloud-login-submit"
              disabled={busy || !newPassword || !newPasswordConfirm}
            >
              {busy ? (
                <span className="cloud-login-spinner" aria-label="Setting password" />
              ) : (
                "Set password & sign in"
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="cloud-login-overlay">
      <div className="cloud-login-card">
        <div className="cloud-login-logo">
          <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
            <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
          </svg>
        </div>
        <h1 className="cloud-login-title">Excalidraw SGDS</h1>
        <p className="cloud-login-subtitle">Sign in to access your drawings</p>

        <form onSubmit={handleCredentials} className="cloud-login-form">
          {displayError && (
            <div className="cloud-login-error" role="alert">
              {displayError}
            </div>
          )}

          <div className="cloud-login-field">
            <label htmlFor="cloud-username">Username</label>
            <input
              id="cloud-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              disabled={busy}
            />
          </div>

          <div className="cloud-login-field">
            <label htmlFor="cloud-password">Password</label>
            <input
              id="cloud-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={busy}
            />
          </div>

          <button
            type="submit"
            className="cloud-login-submit"
            disabled={busy || !username || !password}
          >
            {busy ? (
              <span className="cloud-login-spinner" aria-label="Signing in" />
            ) : (
              "Sign in"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};
